import {useViewer} from '../../state/viewer/hooks';
import {billingStore, useWBAgentBillingAccess} from './billing';
import {selectActiveThread, useStoreSelector} from './store';
import type {Thread} from './store/thread';

/** ReadonlyInputs are the signals that determine whether the chat is locked. */
type ReadonlyInputs = {
  accessible: boolean;
};

/**
 * computeAgentChatReadonly derives whether the chat is locked for writes from
 * its inputs. Billing access is the only input today; other lock reasons join
 * here so every gate updates together.
 */
export function computeAgentChatReadonly(inputs: ReadonlyInputs): boolean {
  return !inputs.accessible;
}

/**
 * useAgentChatReadonly reports whether the chat is locked, so modifying actions
 * must be blocked and the UI shown as disabled. React counterpart of
 * isAgentChatReadonly.
 */
export function useAgentChatReadonly(): boolean {
  return computeAgentChatReadonly({
    accessible: useWBAgentBillingAccess().accessible,
  });
}

/**
 * isAgentChatReadonly reads the current lock state for non-React callers, so
 * imperative writers gate on readonly instead of on billing access directly.
 */
export function isAgentChatReadonly(): boolean {
  return computeAgentChatReadonly({accessible: billingStore.isAccessible()});
}

/**
 * viewerOwnsThread tells whether the viewer may write to `thread`. Another
 * user's shared thread is read-only. A chat started in this browser is the
 * viewer's, and with no thread the next message starts one.
 */
export function viewerOwnsThread(
  thread: Thread | undefined,
  viewerUsername: string | undefined
): boolean {
  const ownerUsername = thread?.ownerUsername ?? null;
  return ownerUsername == null || ownerUsername === viewerUsername;
}

/** useViewerOwnsActiveThread tells whether the viewer may write to the active thread. */
export function useViewerOwnsActiveThread(): boolean {
  const viewerUsername = useViewer()?.username;
  return useStoreSelector(state =>
    viewerOwnsThread(selectActiveThread(state), viewerUsername)
  );
}

/**
 * useActiveThreadReadonly reports whether writes to the active thread are
 * blocked: the chat is locked, or another user owns the thread.
 */
export function useActiveThreadReadonly(): boolean {
  const ownsThread = useViewerOwnsActiveThread();
  return useAgentChatReadonly() || !ownsThread;
}
